import { useMemo, useState } from 'react'
import { buildTimeline, filterTimeline, markLabel, TIMELINE_FILTERS, type TimelineEntry, type TimelineFilterKey, type TimelineKind } from '@aurora/core'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import { activeChild, authorName, members } from '../store/selectors'
import { formatFriendlyDate, formatTime, relativeDay, todayISO } from '../lib/dates'
import { domainColor, domainLabel } from '../lib/rubric'
import { madeSummary } from '../lib/games'
import { EmptyState } from '../components/ui/EmptyState'
import { TellAurora } from '../components/today/TellAurora'

/**
 * The family timeline — the home page.
 *
 * Everything recorded or done, newest first, grouped by day, each entry
 * signed by the parent who did it. This is the shared notebook the family
 * used to keep on paper: one place where either parent can see what the
 * other did this morning, what Sapling made of the week, and what moved.
 *
 * The selector lives in @aurora/core and the phone renders the same result,
 * so the two screens never disagree about what happened.
 */

type Filter = TimelineFilterKey
const FILTERS = TIMELINE_FILTERS

export function TimelineView() {
  const child = useAuroraStore(activeChild)
  const [filter, setFilter] = useState<Filter>('all')
  const captures = useAuroraStore((s: any) => s.captures)
  const blocks = useAuroraStore((s: any) => s.blocks)
  const days = useAuroraStore((s: any) => s.days)
  const reviews = useAuroraStore((s: any) => s.reviews)
  const artifacts = useAuroraStore((s: any) => s.artifacts)
  const levelHistory = useAuroraStore((s: any) => s.levelHistory)
  const skills = useAuroraStore((s: any) => s.skills)
  const manyPeople = useAuroraStore((s: any) => members(s).length > 1)

  const timeline = useMemo(
    () => (child ? buildTimeline({
      childId: child._id, timezone: child.timezone,
      captures, blocks, days, reviews, artifacts, levelHistory, skills,
    }) : []),
    [child, captures, blocks, days, reviews, artifacts, levelHistory, skills],
  )
  const shown = useMemo(
    () => filterTimeline(timeline, new Set(FILTERS.find((f) => f.key === filter)?.kinds ?? [])),
    [timeline, filter],
  )
  if (!child) return null

  const today = todayISO(child.timezone)
  const weekAgo = new Date(`${today}T00:00:00Z`).getTime() - 6 * 86_400_000
  const thisWeek = timeline.reduce((n, d) => n + (new Date(`${d.date}T00:00:00Z`).getTime() >= weekAgo ? d.entries.length : 0), 0)

  return (
    <div className="page timeline">
      <header className="page-head reveal" style={{ '--i': 0 } as React.CSSProperties}>
        <div className="page-eyebrow">Family timeline</div>
        <h1 className="page-title">What has been happening</h1>
        {thisWeek > 0 && (
          <p className="timeline-sub">
            {thisWeek} {thisWeek === 1 ? 'thing' : 'things'} recorded in the last seven days
          </p>
        )}
      </header>

      <TellAurora />

      <div className="timeline-filters reveal" style={{ '--i': 1 } as React.CSSProperties} role="tablist" aria-label="Show">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={filter === f.key}
            className={`chip${filter === f.key ? ' is-active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState
          title={timeline.length ? 'Nothing of that kind yet' : 'Nothing recorded yet'}
          body={
            timeline.length
              ? 'Try another filter, or record something after the next activity.'
              : `Mark a block, hold the mic after an activity, or take a photo of ${child.name}'s work. It all lands here, with who did it.`
          }
        />
      ) : (
        <ol className="timeline-days">
          {shown.map((day, di) => (
            <li key={day.date} className="timeline-day reveal" style={{ '--i': Math.min(di + 2, 8) } as React.CSSProperties}>
              <div className="timeline-dayhead">
                <span className="timeline-dayrel">{relativeDay(day.date, today)}</span>
                <span className="timeline-daydate">{formatFriendlyDate(day.date, child.timezone)}</span>
                <span className="timeline-daycount tnum">{day.entries.length}</span>
              </div>
              <ol className="timeline-entries">
                {day.entries.map((e) => (
                  <Entry key={e.id} entry={e} timezone={child.timezone} showAuthor={manyPeople} />
                ))}
              </ol>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

const KIND_LABEL: Record<TimelineKind, string> = {
  capture: 'Note', mark: 'Marked', note: 'Note', photo: 'Photo', review: 'Check-in',
  sapling: 'Sapling', level: 'Moved', material: 'Made',
}

function Entry({ entry, timezone, showAuthor }: { entry: TimelineEntry; timezone?: string; showAuthor: boolean }) {
  const who = useAuroraStore((s: any) => authorName(s, entry.authorId))
  const bySapling = !entry.authorId && (entry.kind === 'sapling' || entry.kind === 'material' || (entry.kind === 'level' && entry.status !== 'parent'))
  const signature = bySapling ? 'Sapling' : showAuthor ? who : undefined
  // A thing made reads as it is now (a deck's size and pace), not as it was described the day it was made.
  const made = useAuroraStore((s: any) => (entry.kind === 'material' && s.artifacts[entry.ref.artifactId ?? '']?.kind === 'deck' ? madeSummary(s.artifacts[entry.ref.artifactId!]) : undefined)) as string | undefined
  const body = made ?? entry.body

  const open = () => {
    const s = auroraStore.getState() as any
    const r = entry.ref
    if (r.artifactId) {
      if (s.artifacts[r.artifactId]?.kind === 'activity') { s.openGame(r.artifactId); return }
      s.selectArtifact(r.artifactId); s.setView('library'); return
    }
    if (r.skillKey) { s.setView('child'); return }
    if (r.reviewId) { s.setView('story'); return }
    if (r.date) { s.selectDate(r.date); s.setView('today'); return }
    if (r.blockId) { s.setView('today'); return }
    s.setView('today')
  }

  return (
    <li
      className={`tl-entry tl-${entry.kind}`}
      style={{ '--domain-color': entry.domainKey ? domainColor(entry.domainKey) : undefined } as React.CSSProperties}
    >
      <span className="tl-glyph" aria-hidden="true"><Glyph kind={entry.kind} status={entry.status} /></span>
      <button className="tl-body" onClick={open}>
        <span className="tl-meta">
          <span className="tl-kind">{KIND_LABEL[entry.kind]}</span>
          {entry.domainKey && <span className="tl-domain">{entry.domainName ?? domainLabel(entry.domainKey)}</span>}
          {signature && <span className={`tl-who${bySapling ? ' is-sapling' : ''}`}>{signature}</span>}
          <span className="tl-time tnum">{formatTime(entry.at, timezone)}</span>
        </span>
        <span className="tl-title">
          {entry.title}
          {entry.kind === 'mark' && entry.status && (
            <span className={`tl-status is-${entry.status}`}>{markLabel(entry.status) || entry.status}</span>
          )}
          {entry.kind === 'level' && typeof entry.level === 'number' && (
            <span className="tl-level">now level {entry.level}</span>
          )}
          {entry.kind === 'material' && entry.status && <span className="tl-status">{entry.status}</span>}
        </span>
        {entry.imageUrl && <img className="tl-photo" src={entry.imageUrl} alt={entry.title} loading="lazy" />}
        {body && <span className="tl-text">{body}</span>}
      </button>
    </li>
  )
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, width: 13, height: 13,
}
function Glyph({ kind, status }: { kind: TimelineKind; status?: string }) {
  switch (kind) {
    case 'mark':
      if (status === 'skipped') return <svg {...S}><path d="M6 6l12 12M18 6L6 18" /></svg>
      if (status === 'partial') return <svg {...S}><path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" stroke="none" /></svg>
      return <svg {...S} strokeWidth={2.4}><path d="M4 12.5l5 5L20 6.5" /></svg>
    case 'capture': return <svg {...S}><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v4" /></svg>
    case 'note': return <svg {...S}><path d="M4 20h4l10-10-4-4L4 16z" /></svg>
    case 'photo': return <svg {...S}><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="12" cy="12" r="3.5" /></svg>
    case 'review': return <svg {...S}><path d="M4 6h16M4 12h10M4 18h7" /></svg>
    case 'sapling': return <svg {...S}><path d="M12 21v-8M12 13c-4 0-7-3-7-7 4 0 7 3 7 7zM12 13c4 0 7-3 7-7-4 0-7 3-7 7z" /></svg>
    case 'level': return <svg {...S}><path d="M5 17l5-5 4 4 5-7" /></svg>
    case 'material': return <svg {...S}><path d="M12 4l1.5 4L18 9.5 13.5 11 12 15l-1.5-4L6 9.5 10.5 8z" /></svg>
  }
}
